import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { Typography } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { palette } from "../../../theme/palette";
import { NONE } from "../../../consts/strings";
import { daysOfWeek } from "../../../consts/driverManagement";
import {
  DR_DRIVER_TYPE,
  ODF_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../../../consts/driverTypes";

import { selectedDriverSelector } from "../../../reducers/ownApp/selectors";
import {
  driverEditSelector,
  driverProfileSelector,
} from "../../../reducers/driverManagement/selectors";
import {
  setIsDriverEditWorkScheduleValid,
  updateDriverDayEdit,
} from "../../../reducers/driverManagement/thunkActions";

import { WorkScheduleModalContent } from "./WorkScheduleModalContent";
import { View } from "../../View";

const useStyles = makeStyles({
  container: {
    flex: 1,
    margin: "0.5%",
    padding: "0 0 0.25% 0",
    maxWidth: "93.5rem",
    maxHeight: "99%",
    backgroundColor: palette.white,
    borderRadius: "0.4rem",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    zIndex: 101,
  },
  hidden: {
    display: "none",
  },
  top: {
    borderRadius: "0.4rem 0.4rem 0rem 0rem",
    backgroundColor: palette.dpdLightGrey,
  },
  title: {
    padding: "1rem 1.6rem",
  },
  rowHeader: {
    color: palette.white,
    textAlign: "center",
    padding: "0.84rem 0",
  },
  rowHeaderSmall: {
    color: palette.white,
    textAlign: "center",
    padding: "0.25rem 0",
    fontSize: 13,
  },
  bottom: {
    display: "flex",
    marginTop: "0.5rem",
  },
});

const DAYS_STATE = {
  0: false,
  1: false,
  2: false,
  3: false,
  4: false,
  5: false,
  6: false,
};

export const WorkScheduleModal = ({ hidden }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const driverEdit = useSelector(driverEditSelector);
  const selectedDriver = useSelector(selectedDriverSelector);
  const selectedDriverCode = (driverEdit || selectedDriver)?.driverCode;
  const hasSelectedDriver = !!selectedDriverCode;
  const { driverType: driverProfileType } =
    useSelector(driverProfileSelector(selectedDriverCode)) || {};
  const [errorDays, setErrorDays] = useState(DAYS_STATE);

  const isRobot = ROBOT_DRIVER_TYPE === driverProfileType;
  const disabledControls = {
    plannedOdf: DR_DRIVER_TYPE !== driverProfileType,
    driverCode: ODF_DRIVER_TYPE !== driverProfileType,
    deliveryStopTime: isRobot,
    collectionStopTime: isRobot,
    startAtSatellite: isRobot,
    endAtSatellite: isRobot,
  };
  const hiddenControls = {
    plannedOdf: ![ODF_DRIVER_TYPE, DR_DRIVER_TYPE].includes(driverProfileType),
    robotTypeCode: true,
    vehicleType: true,
  };

  useEffect(() => {
    if (!hasSelectedDriver) return;
    setErrorDays(DAYS_STATE);
  }, [hasSelectedDriver]);

  const onValidationCallback = useCallback(
    ({ dayOfWeek, hasErrors }) => {
      if (errorDays[dayOfWeek] !== hasErrors) {
        const newErrorDays = { ...errorDays, [dayOfWeek]: hasErrors };
        const isValid = !Object.values(newErrorDays).includes(true);
        setErrorDays(newErrorDays);
        dispatch(setIsDriverEditWorkScheduleValid(isValid));
      }
    },
    [dispatch, errorDays]
  );

  // Promise must be returned to prevent multiple updated running simultaneously
  const onChange = useCallback(
    ({ dayOfWeek, name, value }) =>
      dispatch(
        updateDriverDayEdit({
          dayOfWeek,
          name,
          value: value === NONE ? null : value,
        })
      ),
    [dispatch]
  );

  if (!hasSelectedDriver) return null;
  else
    return (
      <View className={clsx(styles.container, hidden && styles.hidden)}>
        <View className={styles.top}>
          <Typography className={styles.title}>
            {!driverEdit ? "DRIVER WORK SCHEDULE" : "EDIT DRIVER WORK SCHEDULE"}
          </Typography>
        </View>
        <View className={styles.bottom}>
          {daysOfWeek.map((dayOfWeek) => (
            <View key={`${dayOfWeek}`}>
              <WorkScheduleModalContent
                disabledControls={disabledControls}
                hiddenControls={hiddenControls}
                dayOfWeek={dayOfWeek}
                onValidation={onValidationCallback}
                onChange={onChange}
              />
            </View>
          ))}
        </View>
      </View>
    );
};

export default WorkScheduleModal;
